import {
  IconDelete,
  IconEdit,
  IconVisibility,
  IconContentCopy,
  TableOverflowButton,
  type ContextMenuOption,
} from "@cdkglobal/radial";
import { isCustomRowActionDisabled } from "../helpers";
import { RowData } from "../../../types";
import { useIntl } from "react-intl";
import { ICellRendererParams } from "ag-grid-community";
import { useUIStateContext } from "../../../contexts/UIStateContext";
import { useUserContext } from "../../../contexts/UserContext";

type ActionCellRendererProps = ICellRendererParams & {
  rowDataList: RowData[];
  setupName: string;
  isEditDisabled: boolean;
  isDeleteDisabled: boolean;
  isSetupActionsLocked: boolean;
  handleView: (rowData: RowData) => void;
  handleEdit: (rowData: RowData) => void;
  handleCopy: (rowData: RowData) => void;
  handleDelete: (rowData: RowData) => void;
};

export default function ActionCellRenderer({
  data: rowData,
  rowDataList,
  setupName,
  isEditDisabled,
  isDeleteDisabled,
  isSetupActionsLocked,
  handleView,
  handleEdit,
  handleCopy,
  handleDelete,
}: ActionCellRendererProps): JSX.Element {
  const intl = useIntl();
  const { recordPageToast } = useUIStateContext();
  const { permissions } = useUserContext();
  const viewLabel = intl.formatMessage({
    id: "button.view",
    defaultMessage: "View",
  });
  const editLabel = intl.formatMessage({
    id: "button.edit",
    defaultMessage: "Edit",
  });
  const copyLabel = intl.formatMessage({
    id: "button.copy",
    defaultMessage: "Copy",
  });
  const deleteLabel = intl.formatMessage({
    id: "button.delete",
    defaultMessage: "Delete",
  });
  const isViewDisabled =
    Boolean(recordPageToast.variant === "neutral" && recordPageToast.message) ||
    !permissions.setupPermission.hasView;

  const isCustomEditDisabled = Boolean(
    isViewDisabled ||
    isEditDisabled ||
    isCustomRowActionDisabled(rowData, rowDataList, setupName) ||
    !permissions.setupPermission.hasManage,
  );

  const overflowOptions: ContextMenuOption[] = permissions.setupPermission
    .hasManage
    ? [
        {
          id: "view",
          label: viewLabel,
          icon: <IconVisibility />,
          isDisabled: isViewDisabled,
        },
        {
          id: "edit",
          label: editLabel,
          icon: <IconEdit />,
          isDisabled: isCustomEditDisabled,
        },
      ]
    : [
        {
          id: "view",
          label: viewLabel,
          icon: <IconVisibility />,
          isDisabled: isViewDisabled,
        },
      ];

  if (!isSetupActionsLocked && permissions.setupPermission.hasManage) {
    overflowOptions.push(
      {
        id: "copy",
        label: copyLabel,
        icon: <IconContentCopy />,
        isDisabled: isCustomEditDisabled,
      },
      {
        id: "delete",
        label: deleteLabel,
        icon: <IconDelete />,
        isDisabled: isDeleteDisabled || isCustomEditDisabled,
      },
    );
  }

  const handleSelect = (option: ContextMenuOption) => {
    switch (option.id) {
      case "view":
        handleView(rowData);
        break;
      case "edit":
        handleEdit(rowData);
        break;
      case "copy":
        handleCopy(rowData);
        break;
      case "delete":
        handleDelete(rowData);
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <TableOverflowButton
        ariaLabel="Row actions"
        dataTestId="row-actions-overflow-button"
        contextMenuOptions={overflowOptions}
        onSelect={handleSelect}
      />
    </div>
  );
}
